import React, { useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	buildNavigationAriForMenuData,
	buildNavigationAriForSidebarData,
} from '@atlassian/navigation-common/navigation-ari';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import type { GlobalSideNavigationAGGRelayConnectionProps } from '../common/types';
import { type GlobalSideNavigationAGGRelayConnectionQuery } from './__generated__/GlobalSideNavigationAGGRelayConnectionQuery.graphql';

export function GlobalSideNavigationAGGRelayConnection(
	props: GlobalSideNavigationAGGRelayConnectionProps,
): React.JSX.Element {
	// We need to use RelayEnvironmentProvider because the platform-version points to AGG, which is where the data can be accessed from.
	return (
		<RelayEnvironmentProvider>
			<RawGlobalSideNavigationAGGRelayConnection {...props} />
		</RelayEnvironmentProvider>
	);
}

/**
 * This component is exposed for testing purposes, but is wrapped by GlobalSideNavigationAGGRelayConnection
 * @example ```
 * <RelayEnvironmentProvider environment={mockEnv}>
 *   <RawGlobalSideNavigationAGGRelayConnection entityAri={} ownerAri={}>
 *     {({queryRef}) => <GlobalSideNavigation networkStorageParams={{entityAri, ownerAri, queryRef}} ... />}
 *   </RawGlobalSideNavigationAGGRelayConnection>
 * </RelayEnvironmentProvider>
 * ```
 */
export function RawGlobalSideNavigationAGGRelayConnection(
	props: GlobalSideNavigationAGGRelayConnectionProps,
): React.JSX.Element {
	const { children, activationId, entityAri, firstPartyApp, ownerAri, siteId } = props;
	const queryVariables = useMemo(
		() => ({
			cloudId: siteId || '',
			entityAriUserSideNavSettings: entityAri || '',
			fetchGlobalShortcutsAvailableProducts: Boolean(siteId && ownerAri),
			fetchOldEntityRecord: !!entityAri, // is false when entityAri is undefined, null, 0 or '' (false-y)
			navPlatMenuDataEntityAri: buildNavigationAriForMenuData({
				activationId: activationId || '',
				firstPartyApp,
				siteId: siteId || '',
			}),
			navPlatSidebarDataEntityAri: buildNavigationAriForSidebarData({
				firstPartyApp,
				siteId: siteId || '',
				activationId: activationId || '',
			}),
			ownerAri,
		}),
		[activationId, firstPartyApp, entityAri, ownerAri, siteId],
	);

	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	// When GSN is enabled in Confluence (fg navx-global-side-nav-confluence), the SSR
	// preloader (preloadPlatformNavigationQuery) populates the Relay cache before render.
	// Using 'store-or-network' here means useLazyLoadQuery reads from cache synchronously
	// when data is available (no suspension, no blocking), and only falls back to network
	// when the cache is empty. This avoids the server-side suspension (React error 419)
	// that occurs when the cache is empty on SSR routes.
	// On non-Confluence consumers (Jira, Townsquare) the gate is off, so `fetchPolicy` is
	// left `undefined` and Relay applies its default ('store-or-network'); behaviour is
	// unchanged for them, and the Suspense boundary catches any suspend on a cache miss.
	//
	// TEMPORARY BRIDGE (navx-global-side-nav-confluence gate): use store-or-network so that
	// Confluence's SSR preloader (preloadPlatformNavigationQuery) can populate the Relay store
	// before renderToPipeableStream and have useLazyLoadQuery read from cache synchronously.
	// This avoids suspension during SSR without blocking the preloader batch.
	//
	// Long-term: when Confluence's Relay+SSR infrastructure supports usePreloadedQuery()
	// (planned August 2026, cc-relay-adoption team), Confluence should:
	//   1. Remove fetchQuery+loadQuery from preloadPlatformNavigationQuery
	//   2. Wire the query ref through the route entry-point getPreloadProps
	//   3. Replace GlobalSideNavigationAGGRelayConnection with a direct usePreloadedQuery
	//      call in PlatformNavigationComponent
	//   4. Remove this store-or-network gate from the platform package
	//
	// See: https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/356117
	const fetchPolicy = fg('navx-global-side-nav-confluence') ? 'store-or-network' : undefined;

	// TODO: Update this query to just fetch "...GlobalSideNavigation",
	//       and use skip(if) to conditionally skip the old fragments when GTN/GRT is on.
	//       Might be better to add a new prop (opt-in) that flips the behaviour. Then
	//       we can easily see which apps haven't opted-in.
	const gsnData = useLazyLoadQuery<GlobalSideNavigationAGGRelayConnectionQuery>(
		graphql`
			query GlobalSideNavigationAGGRelayConnectionQuery(
				$cloudId: ID!
				$entityAriUserSideNavSettings: ID!
				$fetchGlobalShortcutsAvailableProducts: Boolean!
				$fetchOldEntityRecord: Boolean!
				$navPlatMenuDataEntityAri: ID!
				$navPlatSidebarDataEntityAri: ID!
				$ownerAri: ID!
			) {
				...useNetworkSideNavUserData
					@dangerously_unaliased_fixme
					@include(if: $fetchOldEntityRecord)
				...useGlobalShortcutsDataRelay
					@dangerously_unaliased_fixme
					@include(if: $fetchGlobalShortcutsAvailableProducts)
				navPlatL1MenuData: settings_navigationCustomisation(
					entityAri: $navPlatMenuDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
			}
		`,
		queryVariables,
		{ fetchPolicy },
	);
	/* eslint-enable @atlassian/relay/must-colocate-fragment-spreads */

	return <>{children({ queryRef: gsnData })}</>;
}
